import posthog from "posthog-js";
import { config } from "@/config/env";

const enabled = !!config.posthogToken;
const CONSENT_STORAGE_KEY = "campnerd_tracking_consent";
const CONSENT_STORAGE_TTL_MS = 365 * 24 * 60 * 60 * 1000;
export const TRACKING_CONSENT_RESET_EVENT = "campnerd:tracking-consent-reset";

export type TrackingConsent = "granted" | "denied";

let initialized = false;
let consentRequired = false;
const storedConsent = readConsent();
let consent: TrackingConsent | null = storedConsent?.value ?? null;
let consentExpiresAt: number | null = storedConsent?.expiresAt ?? null;
let currentAid: string | null = null;
let currentCampground: string | null = null;

interface StoredConsent {
  value: TrackingConsent;
  expiresAt: number;
}

function readConsent(): StoredConsent | null {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !("value" in parsed) ||
      (parsed.value !== "granted" && parsed.value !== "denied") ||
      !("expiresAt" in parsed) ||
      typeof parsed.expiresAt !== "number" ||
      parsed.expiresAt <= Date.now()
    ) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    return { value: parsed.value, expiresAt: parsed.expiresAt };
  } catch {
    clearStoredConsent();
    return null;
  }
}

function storeConsent(value: TrackingConsent) {
  const expiresAt = Date.now() + CONSENT_STORAGE_TTL_MS;
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({ value, expiresAt }));
  } catch {
    // Consent still applies for the current page when storage is unavailable.
  }
  return expiresAt;
}

function clearStoredConsent() {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Consent is still reset for the current page when storage is unavailable.
  }
}

function expireConsentIfNeeded() {
  if (consentExpiresAt === null || consentExpiresAt > Date.now()) return false;

  consent = null;
  consentExpiresAt = null;
  clearStoredConsent();
  syncPostHogConsent();
  window.dispatchEvent(new Event(TRACKING_CONSENT_RESET_EVENT));
  return true;
}

function canCapture() {
  expireConsentIfNeeded();
  return !consentRequired || consent === "granted";
}

function registerContext() {
  if (!initialized) return;
  if (currentAid) {
    posthog.register({ aid: currentAid });
  } else {
    posthog.unregister("aid");
  }
  if (currentCampground) posthog.register({ campground: currentCampground });
}

function syncPostHogConsent() {
  if (!initialized) return;

  if (!consentRequired) {
    posthog.clear_opt_in_out_capturing();
    posthog.startSessionRecording();
    return;
  }

  if (consent === "granted") {
    posthog.opt_in_capturing({ captureEventName: false });
    registerContext();
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
  }
}

function initializeAnalytics() {
  if (!enabled || initialized || !canCapture()) return;

  posthog.init(config.posthogToken!, {
    api_host: config.posthogHost ?? "https://eu.i.posthog.com",
    autocapture: false, // iframe widget — DOM click capture is noisy/risky
    capture_pageview: false, // handled manually by AnalyticsLifecycle
    capture_pageleave: false, // not meaningful in iframe
    session_recording: {
      maskAllInputs: true, // guard against accidental recording of billing fields
    },
  });
  initialized = true;
  registerContext();
  syncPostHogConsent();
}

export type AnalyticsEvent =
  | "search_started"
  | "alternative_dates_viewed"
  | "alternative_dates_selected"
  | "accommodation_selected"
  | "extras_submitted"
  | "details_submitted"
  | "checkout_submitted"
  | "checkout_failed"
  | "booking_completed";

export const analytics = {
  initialize() {
    initializeAnalytics();
  },

  configureConsentRequirement(required: boolean) {
    consentRequired = required;
    syncPostHogConsent();
  },

  setTrackingConsent(value: TrackingConsent, options: { persist?: boolean } = {}) {
    consent = value;
    consentExpiresAt = options.persist === false ? null : storeConsent(value);
    if (value === "granted") initializeAnalytics();
    syncPostHogConsent();
  },

  resetTrackingConsent() {
    consent = null;
    consentExpiresAt = null;
    clearStoredConsent();
    syncPostHogConsent();
    window.dispatchEvent(new Event(TRACKING_CONSENT_RESET_EVENT));
  },

  getTrackingConsent() {
    canCapture();
    return consent;
  },

  setAid(aid: string | null) {
    currentAid = aid;
    if (!enabled || !initialized || !canCapture()) return;
    if (aid) {
      posthog.register({ aid });
    } else {
      posthog.unregister("aid");
    }
  },

  setCampground(slug: string) {
    currentCampground = slug;
    if (!enabled || !initialized || !canCapture()) return;
    posthog.register({ campground: slug });
  },

  page(path: string) {
    if (!enabled || !initialized || !canCapture()) return;
    posthog.capture("$pageview", { $current_url: path });
  },

  capture(event: AnalyticsEvent, properties?: Record<string, unknown>) {
    if (!enabled || !initialized || !canCapture()) return;
    posthog.capture(event, properties);
  },
};
